const backInStockForm = class extends HTMLElement {
  constructor () {
    super()
    this._initialized = false
  }

  connectedCallback () {
    if (this._initialized) return
    this._initialized = true

    this.product = this.closest('[data-product]')
    this.variantInput = this.product ? this.product.querySelector('input[name="id"]') : null
    this.bisForm = this.querySelector('[data-bis-form]')
    this.bisVariantInput = this.querySelector('[data-bis-variant]')
    this.bisEmailInput = this.querySelector('[data-bis-email]')
    this.successEl = this.querySelector('[data-bis-success-response]')
    this.errorEl = this.querySelector('[data-bis-error-response]')
    this.defaultErrorHtml = this.errorEl ? this.errorEl.innerHTML : ''
    this.app = this.getAttribute('data-app')
    this.submitButton = this.querySelector('[data-bis-submit]')

    if (!this.bisForm || !this.submitButton) { return }

    this.bisInit()
  }

  bisInit () {
    this.submitButton.addEventListener('click', async (event) => {
      event.preventDefault()
      if (this.submitButton.disabled) return

      if (!this.bisEmailInput || !this.bisEmailInput.value || !this.bisEmailInput.checkValidity()) {
        if (this.bisEmailInput) this.bisEmailInput.reportValidity()
        return
      }

      const variantId = Number(this.variantInput ? this.variantInput.value : (this.bisVariantInput && this.bisVariantInput.value))
      const originalLabel = this.submitButton.textContent
      this.submitButton.disabled = true
      this.submitButton.setAttribute('aria-busy', 'true')
      this.submitButton.textContent = 'Submitting...'
      this.errorEl.classList.add('hidden')

      try {
        if (this.app === 'back_in_stock') {
          await this.handleBisAppSubmit(variantId)
        } else if (this.app === 'klaviyo') {
          await this.handleKlaviyoBisSubmit()
        } else {
          throw new Error('app not defined')
        }

        this.bisForm.classList.add('hidden')
        this.successEl.classList.remove('hidden')
        this.errorEl.classList.add('hidden')
      } catch (error) {
        this.bisForm.classList.remove('hidden')
        this.successEl.classList.add('hidden')
        if (this.errorEl) {
          const msg = this.getUserFacingError(error)
          this.errorEl.innerHTML = msg || this.defaultErrorHtml
        }
        this.errorEl.classList.remove('hidden')
      } finally {
        this.submitButton.disabled = false
        this.submitButton.removeAttribute('aria-busy')
        this.submitButton.textContent = originalLabel
      }
    })

    if (this.variantInput && this.bisVariantInput) {
      this.variantInput.addEventListener('change', () => { this.bisVariantInput.value = this.variantInput.value })
    }
  }

  async handleBisAppSubmit (variantId) {
    return new Promise((resolve, reject) => {
      if (typeof window.BISPopover === 'undefined') {
        reject(Error('BISPopover not defined'))
      }

      BISPopover.create(this.bisEmailInput.value, variantId).then(data => {
        if (data.status !== 'OK') {
          reject(Error(data.errors.base[0]))
        } else {
          resolve(data)
        }
      })
    })
  }

  async handleKlaviyoBisSubmit () {
    const companyIdInput = this.bisForm.querySelector('input[name="a"]')
    const companyId = companyIdInput ? String(companyIdInput.value || '').trim() : ''
    const email = this.bisEmailInput ? String(this.bisEmailInput.value || '').trim() : ''
    const variantId = this.bisVariantInput ? String(this.bisVariantInput.value || '').trim() : ''
    if (!companyId) {
      return Promise.reject(new Error('Missing Klaviyo public API key'))
    }
    if (!email) {
      return Promise.reject(new Error('Missing email'))
    }
    if (!variantId) {
      return Promise.reject(new Error('Missing variant id'))
    }
    // Collect [data-bis-property] inputs as custom profile attributes so the
    // Klaviyo profile created by this BIS subscription is tagged with the
    // visitor's market / country (etc.) for future campaign segmentation.
    const properties = {}
    const propertyInputs = this.bisForm.querySelectorAll('[data-bis-property]')
    propertyInputs.forEach((input) => {
      const key = input.getAttribute('data-bis-property')
      const value = (input.value || '').toString().trim()
      if (key && value) properties[key] = value
    })

    const profileAttributes = { email }
    if (Object.keys(properties).length > 0) {
      profileAttributes.properties = properties
    }

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes
            }
          },
          channels: ['EMAIL']
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`
            }
          }
        }
      }
    }

    try {
      const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-06-15'
        },
        body: JSON.stringify(payload)
      })
      if (!response.ok) {
        const txt = await response.text().catch(() => '')
        return Promise.reject(new Error(`Klaviyo BIS request failed: ${response.status}${txt ? ` - ${txt}` : ''}`))
      }
      let parsed = null
      try {
        parsed = await response.clone().json()
      } catch (e) {}
      if (parsed && parsed.success === false) {
        return Promise.reject(new Error(parsed.message || 'Klaviyo BIS request failed'))
      }
      return response
    } catch (error) {
      return Promise.reject(error)
    }
  }

  getUserFacingError (error) {
    const message = (error && error.message ? String(error.message) : '').trim()
    if (!message) return ''
    if (message.includes('Failed to fetch')) {
      return 'Could not reach Klaviyo. This is often caused by an ad/privacy blocker or strict network policy. Please disable blocker for this site and try again.'
    }
    if (message.toLowerCase().includes('klaviyo bis request failed')) {
      return 'Klaviyo rejected the request. Please verify the account public key and Back-in-Stock setup.'
    }
    return message
  }
}

if (typeof window.customElements.get('bis-form') === 'undefined') {
  window.customElements.define('bis-form', backInStockForm)
}